import useAssignUserToReturn from "@babackapp/private-api-client/client/use-assign-user-to-return";
import { useUpdateReturnRequestInCache } from "@babackapp/private-api-client/client/use-invalidate-return-request";
import useUnassignUserToReturn from "@babackapp/private-api-client/client/use-unassign-user-to-return";
import type {
  ReturnRequestDto,
  ShopDto,
} from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@babackapp/ui/components/avatar";
import { Button } from "@babackapp/ui/components/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@babackapp/ui/components/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@babackapp/ui/components/popover";
import { cn } from "@babackapp/ui/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import { resolvePeople, useShopPeople } from "@/lib/people/use-shop-people";
import { useViewerIsSuperAdmin } from "@/lib/rights/use-viewer-is-baback-team";

interface AssignedMembersComboboxProps {
  returnRequest: ReturnRequestDto;
  shop: ShopDto;
}

export default function AssignedMembersCombobox({
  returnRequest,
  shop,
}: AssignedMembersComboboxProps) {
  const updateReturnRequestInCache = useUpdateReturnRequestInCache();
  const t = useTranslation();
  const people = useShopPeople();
  const isSuperAdmin = useViewerIsSuperAdmin();
  // The Baback support is offered to super admins only, and only while active
  // (the API refuses the others); once assigned it stays listed so anyone can
  // unassign it (BAB-3035).
  const assignablePeople = [
    ...people.filter(
      (person) =>
        !person.isBabackSupport ||
        (isSuperAdmin && person.isActive) ||
        returnRequest.assignedUsers.includes(person.userId),
    ),
    // Assigned but no longer resolved: listed so it can be unassigned.
    ...resolvePeople(
      people,
      returnRequest.assignedUsers,
      t("superAdmin.unknownUser"),
    ).filter((person) => !people.includes(person)),
  ];
  const addReturnAssignedUsers = useAssignUserToReturn({
    onSuccess(returnRequest) {
      updateReturnRequestInCache({
        returnRequest,
      });
    },
  });
  const deleteReturnAssignedUsers = useUnassignUserToReturn({
    onSuccess(returnRequest) {
      updateReturnRequestInCache({
        returnRequest,
      });
    },
  });

  const handleMemberSelection = async (userId: string) => {
    const isAssigned = returnRequest.assignedUsers.includes(userId);

    try {
      if (isAssigned) {
        await deleteReturnAssignedUsers.mutateAsync({
          userIds: [userId],
          returnRequestId: returnRequest.id,
          shopId: shop.id,
        });
      } else {
        await addReturnAssignedUsers.mutateAsync({
          params: {
            path: {
              returnRequestId: returnRequest.id,
              shopId: shop.id,
            },
          },
          body: {
            assignedUserIds: [userId],
          },
        });
      }
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour des membres assignés:",
        error,
      );
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className="w-full justify-between"
        >
          {returnRequest.assignedUsers.length > 0
            ? `${returnRequest.assignedUsers.length} membre(s) sélectionné(s)`
            : "Sélectionner des membres"}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0">
        <Command>
          <CommandInput placeholder="Rechercher un membre..." />
          <CommandEmpty> {t("shopViewFilter.noMemebrFound")} </CommandEmpty>
          <CommandList>
            <CommandGroup>
              {assignablePeople.map((member) => {
                const memberName = member.displayName;
                const isAssigned = returnRequest.assignedUsers.includes(
                  member.userId,
                );

                return (
                  <CommandItem
                    key={member.userId}
                    onSelect={() => handleMemberSelection(member.userId)}
                    className="flex items-center"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        isAssigned ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <Avatar className="mr-2 h-6 w-6">
                      <AvatarImage
                        src={member.user?.logoUrl ?? ""}
                        alt={memberName}
                      />
                      <AvatarFallback>{memberName.charAt(0)}</AvatarFallback>
                    </Avatar>
                    {memberName}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
